import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Modal from './Modal'
import Icon from './Icon'
import { shareLink } from '../lib/print'
import { useEntitlements } from '../lib/entitlements'
import { PRESETS, type Preset } from './export/ExportDocument'

/* ============================================================================
   Share.

   Two things people mean by "share", offered together rather than guessed at:

     · send someone the link, so they open the live thing and see it change
     · send someone a document, because they are a promoter with an inbox

   The link is first because it is the one that stays correct. A PDF mailed on
   Tuesday is wrong by Thursday, which is the entire problem Encore exists to
   fix, so the document route is present but second.
   ============================================================================ */

export default function ShareSheet({
  open,
  onClose,
  title,
  subtitle,
  url,
  tourId,
  showId,
}: {
  open: boolean
  onClose: () => void
  /** What is being shared, in words. Used as the share sheet's own title. */
  title: string
  subtitle?: string
  /** Absolute or app-relative link to the live thing. */
  url: string
  tourId: string
  /** Present when sharing a single show, which unlocks the show documents. */
  showId?: string | null
}) {
  const navigate = useNavigate()
  const { permits } = useEntitlements()
  const [said, setSaid] = useState<string | null>(null)

  const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`

  // Show-scoped documents only make sense when a show is in hand.
  const documents = PRESETS.filter((preset) => (preset.needsShow ? Boolean(showId) : true))

  async function send() {
    const result = await shareLink({ title, text: subtitle, url: absolute })
    if (result === 'shared') onClose()
    if (result === 'copied') setSaid('Link copied')
    if (result === 'failed') setSaid('Could not copy the link')
    // 'dismissed' says nothing, deliberately.
  }

  function openExport(preset: Preset) {
    const params = new URLSearchParams({ tour: tourId, preset })
    if (showId) params.set('show', showId)
    navigate(`/export?${params.toString()}`)
    onClose()
  }

  return (
    <Modal open={open} title="Share" onClose={onClose} width={480}>
      <div className="stack" style={{ gap: 18 }}>
        <div className="stack" style={{ gap: 3 }}>
          <span style={{ fontWeight: 600 }}>{title}</span>
          {subtitle && <span className="t-caption">{subtitle}</span>}
        </div>

        <div className="stack" style={{ gap: 7 }}>
          <span className="t-eyebrow">Send the link</span>
          <button type="button" className="btn btn-primary" onClick={send}>
            <Icon name="share" size={15} /> Share link
          </button>
          <p className="t-caption">
            Anyone already on this tour sees the live version, and it stays right when the plan
            changes.
          </p>
          {said && (
            <p className="t-caption" role="status">
              {said}
            </p>
          )}
        </div>

        <div className="stack" style={{ gap: 7 }}>
          <span className="t-eyebrow">Send a document</span>
          <div className="stack" style={{ gap: 6 }}>
            {documents.map((preset) => {
              const locked = Boolean(preset.feature) && !permits(preset.feature!)
              return (
                <button
                  key={preset.value}
                  type="button"
                  className="row panel"
                  onClick={() => openExport(preset.value)}
                  style={{
                    padding: '10px 12px',
                    gap: 10,
                    cursor: 'pointer',
                    textAlign: 'left',
                    opacity: locked ? 0.7 : 1,
                  }}
                >
                  <span className="grow stack" style={{ gap: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600 }}>{preset.label}</span>
                    <span className="t-caption truncate">{preset.blurb}</span>
                  </span>
                  {locked ? (
                    <span className="chip" style={{ color: 'var(--text-tertiary)' }}>
                      Paid
                    </span>
                  ) : (
                    <Icon name="chevronRight" size={15} style={{ color: 'var(--text-tertiary)' }} />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}
